import React, { useEffect, useState } from "react";
import Select from "react-select";
import { RefreshCw } from "lucide-react";
import "../styles/AddItemForm.css";
import "../styles/GRNFormRadio.css";
import "../styles/grnformvalidation.css";
import { IncoBarTagUtils } from "../utils/Incobartag/incobartagUtils";
import { RootState } from "../redux/slices/store";
import { useSelector } from "react-redux";
import { 
  FormData,
  FormErrors,
  PartTagBarcodeFormProps,
  SelectOption
} from '../models/incobar/types';
import { 
  validateIncoBarTag, 
  validateNoOfLabels, 
  isFormValid 
} from '../utils/validation/incoBarTagValidation';
import { 
  formatDateForInput, 
  validateNumericInput, 
  updateFormDataFromPartSelection, 
  prepareSubmitData 
} from '../models/incobar/formUtils';

const initialFormState: FormData = {
  grnNo: "",
  partNo: "",
  partName: "",
  model: "",
  category: "",
  invoiceNo: "",
  invoiceDate: "",
  batchNo: "",
  batchQty: "",
  qualityOnQty: "",
  printedQty: "",
  balanceQty: "",
  packingQty: "",
  noOfLabels: "",
  rack: "",
};

const initialErrorsState: FormErrors = {
  grnNo: "",
  partNo: "",
  rack: "",
  packingQty: "",
  noOfLabels: "",
};

const AddIncoBarTagForm: React.FC<PartTagBarcodeFormProps> = ({
  onSave,
  onCancel,
  isProcessing,
  isEditing,
  editingData,
  existingTableData = [], 
}) => {
  const {
    grnNoOptions,
    fetchGrnNoOptions,
    partNoOptions,
    fetchPartNoOptions,
    fetchSearchPartNoOptions,
    rackOptions,
    fetchRackOptions,
  } = IncoBarTagUtils();
  
  const [updateGRNForAPI, setGRNForAPI] = useState<string>("");
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>(initialErrorsState);
  const [selectedGrnOption, setSelectedGrnOption] = useState<SelectOption | null>(null);
  const [selectedPartOption, setSelectedPartOption] = useState<SelectOption | null>(null);
  const [selectedRackOption, setSelectedRackOption] = useState<SelectOption | null>(null);
  const permission = useSelector((state: RootState) => state.permissions);
  const [uom, setUom] = useState<string>("");
  const hasExistingEntries = existingTableData.length > 0;

  useEffect(() => {
    fetchGrnNoOptions("");
    
    if (isEditing && editingData) {
      setFormData(editingData);
    }
    if (hasExistingEntries && existingTableData[0]?.grnId) {
      const existingGrn = existingTableData[0].grnId;
      const matchedGrn = grnNoOptions.find((g) => g.label === existingGrn.grnNo);
      setFormData(prev => ({
        ...prev,
        grnNo: matchedGrn?.value || "",
      }));
      const grnOption = {
        value: existingGrn._id || existingGrn.number,
        label: existingGrn.grnNo || existingGrn.number
      };
      
      setSelectedGrnOption(grnOption);
      fetchPartNoOptions(grnOption.label);
    }
  }, [hasExistingEntries, existingTableData, updateGRNForAPI]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if ((name === "packingQty" || name === "noOfLabels") && !validateNumericInput(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name in errors) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "noOfLabels" || name === "packingQty") {
      const errorMessage = validateNoOfLabels(
        name === "noOfLabels" ? value : formData.noOfLabels,
        name === "packingQty" ? value : formData.packingQty,
        formData.balanceQty
      );
      
      setErrors(prev => ({
        ...prev,
        noOfLabels: errorMessage
      }));
    }
  };

  const handleSelectChange = async (selectedOption: SelectOption | null, { name }: { name: string }) => {
    const selectedValue = selectedOption?.value || "";
    const selectedPartNo = selectedOption?.label || "";

    if (name === "grnNo") {
      setFormData({
        ...initialFormState,
        grnNo: selectedValue,
      });
      setSelectedGrnOption(selectedOption);
      setSelectedPartOption(null);
      setSelectedRackOption(null);
      setUom("");

      try {
        if (selectedOption) {
          await fetchPartNoOptions(selectedOption.label);
        }
      } catch (error) {
        console.error("Error fetching part options:", error);
      }
    } else if (name === "partNo" && selectedOption) {
      setSelectedPartOption(selectedOption);

      const selectedPart = partNoOptions.find((p) => p.value === selectedValue);
      
      setFormData((prev) => ({
        ...prev,
        partNo: selectedValue,
      }));

      if (selectedPart) {
        setFormData(prev => updateFormDataFromPartSelection(selectedPart, prev));
        setUom(selectedPart.uom);
        fetchRackOptions("", selectedPartNo);
        setSelectedRackOption(null);
        setFormData((prev) => ({
          ...prev,
          rack: "",
        }));
      }
      
    } else if (name === "rack" && selectedOption) {
      setSelectedRackOption(selectedOption);
      
      setFormData((prev) => ({
        ...prev,
        rack: selectedValue,
      }));
    }

    if (name in errors) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
  
    const newErrors = validateIncoBarTag(formData);
    setErrors(newErrors);
    
    if (!isFormValid(newErrors)) {
      return;
    }
  
    try {
      let lastResponse;
      const noOfLabels = Number(formData.noOfLabels);
      
      for (let i = 1; i <= noOfLabels; i++) {
        const dataToSubmit = prepareSubmitData(formData, i, noOfLabels);
        if (i === 1) {
          dataToSubmit.isFirstItem = true;
        }
        
        lastResponse = await onSave(dataToSubmit);
        if (lastResponse?.data?.[0] && i === noOfLabels) {
          const responseData = lastResponse.data[0];
          const updatedPrintedQty = responseData.grnId?.printedQty;
          const updatedBalanceQty = responseData.grnId?.balanceQty;
          setFormData(prevData => ({
            ...prevData,
            printedQty: updatedPrintedQty !== undefined ? updatedPrintedQty.toString() : prevData.printedQty,
            balanceQty: updatedBalanceQty !== undefined ? updatedBalanceQty.toString() : prevData.balanceQty,
            rack: "",
            noOfLabels: ""
          }));
        }
      }
      
      setSelectedRackOption(null);
      setFormSubmitted(false);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const renderGrnField = () => {
    if (hasExistingEntries) {
      return (
        <input
          type="text"
          name="grnNo"
          value={selectedGrnOption?.label || ""}
          readOnly
          className="read-only-input"
        />
      );
    } else {
      return (
        <Select
          name="grnNo"
          options={grnNoOptions}
          value={
            selectedGrnOption ||
            grnNoOptions.find((o) => o.value === formData.grnNo) ||
            null
          }
          onChange={(selectedOption) =>
            handleSelectChange(selectedOption, { name: "grnNo" })
          }
          onInputChange={(inputValue) => {
            fetchGrnNoOptions(inputValue);
          }}
          placeholder="Select GRN"
          className={`react-select-container ${formSubmitted && errors.grnNo ? "error-select" : ""}`}
          classNamePrefix="react-select"
        />
      );
    }
  };

  return (
    <>
      <div className="item-models-header">
        <div className="border grn-header">
          <div className="start-end-layout">
            <div className="breadcrumb">
              <span className="breadcrumb-current">Inventory</span>
              <span className="breadcrumb-separator"> &gt; </span>
              <span className="breadcrumb-item">Part Tag</span>
              <span className="breadcrumb-separator"> &gt; </span>
              <span className="breadcrumb-item">Inco Barcode Tag</span>
            </div>
          </div>
        </div>
      </div>

      <div className="form-container grn-form">
        <form onSubmit={handleSubmit} className="add-item-form">
          <div className="form-grid">
            <div className="form-group">
              <label>
                GRN No:<span className="required">*</span>
              </label>
              <div className="input-error-container">
                {renderGrnField()}
                {formSubmitted && errors.grnNo && (
                  <div className="error-messagegrn">{errors.grnNo}</div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>
                Part No: <span className="required">*</span>
              </label>
              <div className="input-error-container">
                <Select
                  name="partNo"
                  options={partNoOptions}
                  value={
                    selectedPartOption ||
                    partNoOptions.find((o) => o.value === formData.partNo) ||
                    null
                  }
                  onChange={(selectedOption) => {
                      setGRNForAPI("Updating the GRN No");
                      handleSelectChange(selectedOption, { name: "partNo" })
                    }
                  }
                  onInputChange={(inputValue) => {
                    if (formData.grnNo && selectedGrnOption) {
                      fetchSearchPartNoOptions(
                        inputValue,
                        selectedGrnOption.label
                      );
                    }
                  }}
                  placeholder="Select Part No"
                  className={`react-select-container ${formSubmitted && errors.partNo ? "error-select" : ""}`}
                  classNamePrefix="react-select"
                  isDisabled={!formData.grnNo}
                />
                {formSubmitted && errors.partNo && (
                  <div className="error-messagegrn">{errors.partNo}</div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Model:</label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  readOnly
                  className="read-only-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Part Name:</label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="partName"
                  value={formData.partName}
                  onChange={handleChange}
                  readOnly
                  className="read-only-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Category:</label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  readOnly
                  className="read-only-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Invoice No:</label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="invoiceNo"
                  value={formData.invoiceNo}
                  onChange={handleChange}
                  className="read-only-input"
                  readOnly
                />
              </div>
            </div>

            <div className="form-group">
              <label>Invoice Date:</label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleChange}
                  className="read-only-input"
                  readOnly
                />
              </div>
            </div>

            <div className="form-group">
              <label>Batch Qty:</label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="batchQty"
                  value={formData.batchQty}
                  onChange={handleChange}
                  className="read-only-input"
                  readOnly
                />
                <span className="qty-label">{uom}</span>
              </div>
            </div>

            <div className="form-group">
              <label>GRN Qty:</label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="qualityOnQty"
                  value={formData.qualityOnQty}
                  onChange={handleChange}
                  className="read-only-input"
                  readOnly
                />
                <span className="qty-label">{uom}</span>
              </div>
            </div>

            <div className="form-group">
              <label>Printed Qty:</label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="printedQty"
                  value={formData.printedQty}
                  onChange={handleChange}
                  readOnly
                  className="read-only-input"
                />
                <span className="qty-label">{uom}</span>
              </div>
            </div>

            <div className="form-group">
              <label>Balance Qty:</label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="balanceQty"
                  value={formData.balanceQty}
                  onChange={handleChange}
                  readOnly
                  className="read-only-input"
                />
                <span className="qty-label">{uom}</span>
              </div>
            </div>

            <div className="form-group">
              <label>
                Packing Qty:<span className="required">*</span>{" "}
              </label>
              <div className="input-error-container">
                <input
                  type="text"
                  name="packingQty"
                  value={formData.packingQty}
                  onChange={handleChange}
                />
                <span className="qty-label">{uom}</span>
                {formSubmitted && errors.packingQty && (
                  <div className="error-messagegrn">{errors.packingQty}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>
                Rack:<span className="required">*</span>
              </label>
              <div className="input-error-container">
                <Select
                  name="rack"
                  options={rackOptions}
                  value={
                    selectedRackOption ||
                    rackOptions.find((o) => o.value === formData.rack) ||
                    null
                  }
                  onChange={(selectedOption) =>
                    handleSelectChange(selectedOption, { name: "rack" })
                  }
                  placeholder="Select Rack"
                  className={`react-select-container ${formSubmitted && errors.rack ? "error-select" : ""}`}
                  classNamePrefix="react-select"
                />
                {formSubmitted && errors.rack && (
                  <div className="error-messagegrn">Required..!</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>
                No. of Labels:<span className="required">*</span>
              </label>
              <div className="input-error-container input-with-buttons">
                <input
                  type="text"
                  name="noOfLabels"
                  value={formData.noOfLabels}
                  onChange={handleChange}
                  className={errors.noOfLabels ? "error-input" : ""}
                />
                {errors.noOfLabels && (
                  <div className="error-messagegrn">{errors.noOfLabels}</div>
                )}
              </div>
            </div>
            
            {permission.menus.barcode.create ?
            <div className="button-container">
              <button
                type="submit"
                className="button save-button barcode-savebutton"
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Add BarCode"}
              </button>
            </div>
            : <></>}
          </div>
        </form>
      </div>
    </>
  );
};

export default AddIncoBarTagForm;